'use client'

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import type { CSSProperties, JSX, ReactNode } from 'react'

export type LegalConsentLabelProps = {
    variant: 'booking' | 'seminar' | 'invitation'
    therapistName: string
    termsUrl?: string | null
    privacyUrl?: string | null
    linkClassName?: string
    linkStyle?: CSSProperties
    className?: string
}

function ExternalLegalLink({
    href,
    className,
    style,
    children
}: {
    href: string
    className?: string
    style?: CSSProperties
    children: ReactNode
}): JSX.Element {
    return (
        <a href={href} className={className ?? 'underline'} style={style} target="_blank" rel="noreferrer">
            {children}
        </a>
    )
}

export function LegalConsentLabel({
    variant,
    therapistName,
    termsUrl,
    privacyUrl,
    linkClassName,
    linkStyle,
    className
}: LegalConsentLabelProps): JSX.Element {
    const { t } = useComponentTranslation()
    const landingBaseUrl = process.env.NEXT_PUBLIC_LANDING_BASE_URL
    const tebutoAgbUrl = `${landingBaseUrl}/agb`
    const resolvedTermsUrl = termsUrl?.trim() || null
    const resolvedPrivacyUrl = privacyUrl?.trim() || null

    if (variant === 'invitation') {
        return (
            <span className={className}>
                {t('widgets.legalConsent.invitation.prefix')}{' '}
                {resolvedTermsUrl && resolvedPrivacyUrl ? (
                    <>
                        <ExternalLegalLink href={resolvedTermsUrl} className={linkClassName} style={linkStyle}>
                            {t('widgets.legalConsent.therapistTerms')}
                        </ExternalLegalLink>{' '}
                        {t('widgets.legalConsent.and')}{' '}
                        <ExternalLegalLink href={resolvedPrivacyUrl} className={linkClassName} style={linkStyle}>
                            {t('widgets.legalConsent.therapistPrivacy')}
                        </ExternalLegalLink>{' '}
                        {t('widgets.legalConsent.invitation.suffixBoth', { therapist: therapistName })}
                    </>
                ) : resolvedTermsUrl ? (
                    <>
                        <ExternalLegalLink href={resolvedTermsUrl} className={linkClassName} style={linkStyle}>
                            {t('widgets.legalConsent.therapistTerms')}
                        </ExternalLegalLink>{' '}
                        {t('widgets.legalConsent.invitation.suffixTerms', { therapist: therapistName })}
                    </>
                ) : resolvedPrivacyUrl ? (
                    <>
                        <ExternalLegalLink href={resolvedPrivacyUrl} className={linkClassName} style={linkStyle}>
                            {t('widgets.legalConsent.therapistPrivacy')}
                        </ExternalLegalLink>{' '}
                        {t('widgets.legalConsent.invitation.suffixPrivacy', { therapist: therapistName })}
                    </>
                ) : null}
            </span>
        )
    }

    const intro =
        variant === 'seminar' ? t('widgets.legalConsent.seminarIntro') : t('widgets.legalConsent.bookingIntro')

    let middle: ReactNode = null
    if (resolvedTermsUrl && resolvedPrivacyUrl) {
        middle = (
            <>
                {' '}
                {t('widgets.legalConsent.asWellAs')}{' '}
                <ExternalLegalLink href={resolvedTermsUrl} className={linkClassName} style={linkStyle}>
                    {t('widgets.legalConsent.therapistTerms')}
                </ExternalLegalLink>{' '}
                {t('widgets.legalConsent.and')}{' '}
                <ExternalLegalLink href={resolvedPrivacyUrl} className={linkClassName} style={linkStyle}>
                    {t('widgets.legalConsent.therapistPrivacy')}
                </ExternalLegalLink>{' '}
                {t('widgets.legalConsent.ofTherapist', { therapist: therapistName })}
            </>
        )
    } else if (resolvedTermsUrl) {
        middle = (
            <>
                {' '}
                {t('widgets.legalConsent.asWellAs')}{' '}
                <ExternalLegalLink href={resolvedTermsUrl} className={linkClassName} style={linkStyle}>
                    {t('widgets.legalConsent.therapistTerms')}
                </ExternalLegalLink>{' '}
                {t('widgets.legalConsent.ofTherapist', { therapist: therapistName })}
            </>
        )
    } else if (resolvedPrivacyUrl) {
        middle = (
            <>
                {' '}
                {t('widgets.legalConsent.asWellAs')}{' '}
                <ExternalLegalLink href={resolvedPrivacyUrl} className={linkClassName} style={linkStyle}>
                    {t('widgets.legalConsent.therapistPrivacy')}
                </ExternalLegalLink>{' '}
                {t('widgets.legalConsent.ofTherapist', { therapist: therapistName })}
            </>
        )
    }

    return (
        <span className={className}>
            {intro} {t('widgets.legalConsent.acceptPrefix')}{' '}
            <ExternalLegalLink href={tebutoAgbUrl} className={linkClassName} style={linkStyle}>
                {t('widgets.legalConsent.tebutoTerms')}
            </ExternalLegalLink>
            {middle} {t('widgets.legalConsent.acceptSuffix')}
        </span>
    )
}
